import type { ReactNode } from 'react';
import { useCallback, useEffect, useId, useLayoutEffect, useRef } from 'react';
import { Icone } from '@/shared/ui/icones';
import './dialogo.css';

export type LarguraDialogo = 'estreita' | 'padrao' | 'larga';

/**
 * `centro` é a caixa que interrompe para pedir uma decisão: confirmar, apagar,
 * escolher uma coisa só. `lateral` é o painel que encosta à direita e ocupa
 * metade da tela, sobre um véu leve e desfocado (09/10/2026): para LER algo
 * grande — a ficha da oportunidade — e, desde 19/10/2026, para todo formulário
 * de CRIAR ou editar um registro (oportunidade, cliente, tarefa, pedido de
 * exame, tratamento), em que a tela de onde a pessoa veio continua à vista.
 */
export type FormaDialogo = 'centro' | 'lateral';

export type DialogoProps = {
  titulo: string;
  /** Fecha no Esc e no clique do véu. */
  aoFechar: () => void;
  largura?: LarguraDialogo;
  /**
   * No `lateral` a `largura` não vale (é metade da tela) e o título ganha o X
   * de fechar ao lado. A caixa não rola inteira, para o cabeçalho ficar no
   * lugar: com `botoes`, o conteúdo rola num corpo próprio e os botões ficam
   * presos no rodapé; sem, o conteúdo é quem decide o que rola (as abas da
   * ficha da oportunidade).
   */
  forma?: FormaDialogo;
  /** Moldura de consequência: passo difícil de desfazer. */
  consequencia?: boolean;
  /** Rodapé de ações. O primário vai por último no DOM. */
  botoes?: ReactNode;
  /**
   * `alertdialog` quando o diálogo interrompe para exigir uma decisão — o
   * step-up é isso, e o teste dele cobra o papel. `dialog` é o padrão.
   */
  papel?: 'dialog' | 'alertdialog';
  /** `id` do parágrafo que descreve o diálogo, para `aria-describedby`. */
  descricaoId?: string;
  children?: ReactNode;
};

/**
 * O que o teclado alcança dentro da caixa, na ordem em que aparece.
 *
 * Consultado a cada Tab, e não guardado ao abrir: o conteúdo do diálogo muda
 * enquanto ele está aberto — uma lista que carrega, um botão que sai do
 * desabilitado. Lista congelada faria o Tab pular o botão que acabou de
 * habilitar, justamente o que a pessoa está esperando.
 */
const ALCANCAVEIS = [
  'a[href]',
  'button:not(:disabled)',
  'input:not(:disabled)',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/*
 * A PILHA de diálogos abertos.
 *
 * Cada diálogo escuta o teclado da página inteira, e o primeiro a se registrar
 * é o primeiro a ouvir. Com um diálogo aberto POR CIMA de outro (a confirmação
 * de apagar e o visualizador de foto, sobre o card da oportunidade), o Esc
 * fechava o de BAIXO e o Tab levava o foco para os botões dele. Só o do topo
 * trata o teclado; os de baixo esperam a vez.
 */
const pilhaDeDialogos: symbol[] = [];

/**
 * Entra na pilha ao montar e sai ao desmontar. Devolve a pergunta "sou o do
 * topo?", para quem escuta o teclado. Exportado para o visualizador de mídia,
 * que não usa esta casca mas empilha do mesmo jeito.
 */
export function usePilhaDeDialogos(): () => boolean {
  const eu = useRef<symbol>(Symbol('dialogo'));
  useEffect(() => {
    const meu = eu.current;
    pilhaDeDialogos.push(meu);
    return () => {
      const onde = pilhaDeDialogos.lastIndexOf(meu);
      if (onde >= 0) pilhaDeDialogos.splice(onde, 1);
    };
  }, []);
  return useCallback(() => pilhaDeDialogos[pilhaDeDialogos.length - 1] === eu.current, []);
}

function alcancaveisDe(caixa: HTMLElement): HTMLElement[] {
  return Array.from(caixa.querySelectorAll<HTMLElement>(ALCANCAVEIS)).filter(
    // Elemento escondido continua no DOM e continua no seletor. O radio de
    // escolha usa `opacity: 0` de propósito, então o critério é a caixa de
    // layout: sem largura nem altura, não está na tela.
    (elemento) => elemento.offsetWidth > 0 || elemento.offsetHeight > 0,
  );
}

export function Dialogo({
  titulo,
  aoFechar,
  largura = 'padrao',
  forma = 'centro',
  consequencia = false,
  botoes,
  papel = 'dialog',
  descricaoId,
  children,
}: DialogoProps) {
  const id = useId();
  const caixa = useRef<HTMLDivElement>(null);
  const devolverFocoPara = useRef<HTMLElement | null>(null);
  const noTopo = usePilhaDeDialogos();
  /*
   * O `aoFechar` de agora, numa referência.
   *
   * Quem usa o diálogo quase sempre passa uma função nova a cada renderização,
   * e um formulário renderiza a cada letra. Com o `aoFechar` nas dependências,
   * o efeito do foco (abaixo) rodava de novo a cada letra: devolvia o foco a
   * quem abriu o diálogo e o punha no primeiro controle. Entrava uma letra e o
   * foco saía do campo. Pela referência, o Esc chama sempre o mais recente, e
   * o efeito roda só ao abrir e ao fechar.
   */
  const aoFecharAtual = useRef(aoFechar);
  useLayoutEffect(() => {
    aoFecharAtual.current = aoFechar;
  });

  /*
   * O foco fica preso na caixa enquanto ela está aberta.
   *
   * Sem isto o Tab sai do diálogo e continua andando pela tela de baixo, que
   * está inerte atrás do véu: quem navega por teclado tabula por uma dezena de
   * controles invisíveis antes de voltar. É o mesmo problema de um véu que não
   * captura o clique, só para outro dispositivo de entrada.
   *
   * Não usei `<dialog>` nativo por uma razão concreta: o véu dele é
   * `::backdrop`, que fica fora da árvore e não aceita `var(--veu)` do mesmo
   * jeito, e o `showModal` traz a pilha de camada superior — que muda o
   * z-index de todo o produto. Prisão manual é menos código e nenhuma mudança
   * de arquitetura.
   */
  const noTeclado = useCallback(
    (evento: KeyboardEvent) => {
      // Há outro diálogo por cima: o teclado é dele.
      if (!noTopo()) return;
      if (evento.key === 'Escape') {
        evento.stopPropagation();
        aoFecharAtual.current();
        return;
      }
      if (evento.key !== 'Tab' || !caixa.current) return;

      const itens = alcancaveisDe(caixa.current);
      if (itens.length === 0) {
        // Caixa sem nada alcançável: o foco fica nela mesma, e o Tab não tem
        // para onde ir. Sem isto o Tab escaparia para a página.
        evento.preventDefault();
        caixa.current.focus();
        return;
      }

      const primeiro = itens[0]!;
      const ultimo = itens[itens.length - 1]!;
      const atual = document.activeElement;

      if (evento.shiftKey && (atual === primeiro || atual === caixa.current)) {
        evento.preventDefault();
        ultimo.focus();
      } else if (!evento.shiftKey && atual === ultimo) {
        evento.preventDefault();
        primeiro.focus();
      }
    },
    [noTopo],
  );

  useEffect(() => {
    /*
     * Guarda quem tinha o foco e devolve ao fechar.
     *
     * Sem devolver, o foco volta para o começo do documento: a pessoa fecha o
     * diálogo de transferência e o próximo Tab a leva para o menu, não para a
     * conversa em que ela estava.
     */
    devolverFocoPara.current = document.activeElement as HTMLElement | null;

    // O primeiro alcançável, e não a caixa: abrir um diálogo de confirmação
    // com o foco já no campo de motivo é o que a pessoa vem fazer.
    const itens = caixa.current ? alcancaveisDe(caixa.current) : [];
    (itens[0] ?? caixa.current)?.focus();

    document.addEventListener('keydown', noTeclado, true);
    return () => {
      document.removeEventListener('keydown', noTeclado, true);
      devolverFocoPara.current?.focus?.();
    };
  }, [noTeclado]);

  const lateral = forma === 'lateral';
  const classe = [
    'dialogo__caixa',
    lateral ? 'dialogo__caixa--lateral' : null,
    lateral || largura === 'padrao' ? null : `dialogo__caixa--${largura}`,
    consequencia ? 'dialogo__caixa--consequencia' : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={lateral ? 'dialogo dialogo--lateral' : 'dialogo'}
      onClick={(evento) => {
        if (evento.target === evento.currentTarget) aoFechar();
      }}
    >
      <div
        className={classe}
        role={papel}
        aria-modal="true"
        aria-labelledby={id}
        aria-describedby={descricaoId}
        tabIndex={-1}
        ref={caixa}
      >
        {lateral ? (
          <div className="dialogo__cabecalho">
            <h2 className="dialogo__titulo" id={id}>
              {titulo}
            </h2>
            {/*
              O X existe só no lateral: a ficha da oportunidade não tem rodapé
              de botões, e no formulário o rodapé fica longe do título. Fechar
              precisa estar à vista para quem não conhece o Esc nem imagina
              que o clique no fundo desfocado fecha.
            */}
            <button
              type="button"
              className="dialogo__fechar"
              aria-label="Fechar"
              title="Fechar (Esc)"
              onClick={aoFechar}
            >
              <Icone nome="fechar" />
            </button>
          </div>
        ) : (
          <h2 className="dialogo__titulo" id={id}>
            {titulo}
          </h2>
        )}
        {lateral && botoes ? (
          <>
            <div className="dialogo__corpo">{children}</div>
            <div className="dialogo__botoes dialogo__botoes--rodape">{botoes}</div>
          </>
        ) : (
          <>
            {children}
            {botoes ? <div className="dialogo__botoes">{botoes}</div> : null}
          </>
        )}
      </div>
    </div>
  );
}
